import { NavLink, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  CalendarIcon,
  FileTextIcon,
  SettingsIcon,
  CreditCardIcon,
  ClipboardListIcon,
  BugIcon,
} from "lucide-react";
import { useSidebar } from "@/context/SidebarContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { useState, useCallback, useRef, useEffect } from "react";
import React from "react";

export default function Sidebar() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { collapsed, toggle, expand, collapse, clearTimers, isTemporarilyExpanded, isInitialized } = useSidebar();
  const [isHovered, setIsHovered] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const userRole = currentUser?.role || "doctor";

  // Dynamic dashboard route based on role
  const dashboardRoute = userRole === "staff" ? "/frontdesk" : "/";

  const navItems = [
    {
      label: "Dashboard",
      icon: HomeIcon,
      to: dashboardRoute, // ✅ Dynamic
      roles: ["doctor", "billing", "staff", "admin"],
      exactMatch: true
    },
    {
      label: "Patients",
      icon: UsersIcon,
      to: "/patients",
      roles: ["doctor", "billing"]
    },
    {
      label: "Appointments",
      icon: CalendarIcon,
      to: "/appointments",
      roles: ["doctor", "staff"]
    },
    {
      label: "Prescriptions",
      icon: FileTextIcon,
      to: "/prescriptions",
      roles: ["doctor"]
    },
    {
      label: "Billing",
      icon: CreditCardIcon,
      to: "/billing",
      roles: ["doctor", "admin", "staff"]
    },
    {
      label: "Templates",
      icon: ClipboardListIcon,
      to: "/templates",
      roles: ["doctor"]
    },
    {
      label: "Settings",
      icon: SettingsIcon,
      to: "/settings",
      roles: ["admin", "doctor"]
    },
    {
      label: "Debug Logs",
      icon: BugIcon,
      to: "/debug",
      roles: ["admin"]
    },
  ];

  const isActiveRoute = (to: string, exactMatch?: boolean) => {
    if (exactMatch) {
      return location.pathname === to;
    }
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const visibleNavItems = navItems.filter((item) =>
      item.roles.includes(userRole)
  );

  const cleanupTimeouts = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  }, []);

  const handleSidebarMouseEnter = useCallback(() => {
    cleanupTimeouts();
    setIsHovered(true);

    if (collapsed) {
      expand();
    }
  }, [collapsed, expand, cleanupTimeouts]);

  const handleSidebarMouseLeave = useCallback(() => {
    cleanupTimeouts();
    setIsHovered(false);
    setActiveTooltip(null);

    if (isTemporarilyExpanded) {
      collapse();
    }
  }, [isTemporarilyExpanded, collapse, cleanupTimeouts]);

  const handleItemMouseEnter = useCallback((label: string) => {
    if (collapsed && !isHovered) {
      cleanupTimeouts();
      setActiveTooltip(label);
    }
  }, [collapsed, isHovered, cleanupTimeouts]);

  const handleItemMouseLeave = useCallback(() => {
    cleanupTimeouts();
    setActiveTooltip(null);
  }, [cleanupTimeouts]);

  useEffect(() => {
    return () => {
      cleanupTimeouts();
      clearTimers();
    };
  }, [cleanupTimeouts, clearTimers]);

  return (
      <aside
          aria-label="Sidebar navigation"
          className={cn(
              "fixed top-[60px] left-0 h-[calc(100vh-60px)] z-40 border-r border-border overflow-y-auto overflow-x-hidden sidebar-ideal sidebar-no-shift bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-foreground))] shadow-sm",
              !isInitialized && "sidebar-loading",
              collapsed && !isHovered
                  ? "w-12"
                  : "w-44 lg:w-[180px] xl:w-[200px] 2xl:w-[210px]"
          )}
          onMouseEnter={handleSidebarMouseEnter}
          onMouseLeave={handleSidebarMouseLeave}
      >
        <nav className="py-4" role="navigation" aria-label="Main navigation">
          <ul className="space-y-1">
            {visibleNavItems.map(({ label, icon: Icon, to, exactMatch }) => {
              const isActive = isActiveRoute(to, exactMatch);
              const showTooltip = collapsed && !isHovered && activeTooltip === label;
              const isDivider = label === 'Settings' || label === 'Debug Logs';

              return (
                  <React.Fragment key={to}>
                    {isDivider && (
                        <li>
                          <div className="my-2 border-t border-border mx-4" />
                        </li>
                    )}
                    <li key={label}>
                      <NavLink
                          to={to}
                          className={cn(
                              "flex items-center text-sm font-medium group relative sidebar-focus sidebar-item-hover overflow-hidden transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-inset",
                              collapsed && !isHovered ? "px-2 py-3 justify-center text-xs" : "px-6 py-3 text-xs",
                              isActive
                                  ? "bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-active))] font-semibold rounded-lg sidebar-item-active"
                                  : "text-[hsl(var(--sidebar-foreground)/0.65)] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-foreground))]"
                          )}
                          aria-current={isActive ? "page" : undefined}
                          onMouseEnter={() => handleItemMouseEnter(label)}
                          onMouseLeave={handleItemMouseLeave}
                          style={isActive ? { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)' } : {}}
                      >
                        <Icon className={cn(
                            "flex-shrink-0 transition-all duration-150 sidebar-icon",
                            collapsed && !isHovered ? "mx-auto w-4 h-4" : "mr-3 w-4 h-4",
                            isActive ? "text-[hsl(var(--sidebar-active))]" : ""
                        )} />
                        <span className={cn(
                            "transition-all duration-200 whitespace-nowrap sidebar-text-smooth",
                            collapsed && !isHovered ? "sidebar-text-hidden" : "sidebar-text-visible"
                        )}>
                      {label}
                    </span>
                        {showTooltip && (
                            <div className="sidebar-tooltip fixed left-20 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg border border-border whitespace-nowrap z-[100] pointer-events-none">
                              {label}
                              <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-popover" />
                            </div>
                        )}
                      </NavLink>
                    </li>
                  </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </aside>
  );
}